// Saving the campaign plan: materialize audiences and creatives, write the plan,
// recover a write whose response was lost, and validate before any of it.

import { cacheCampaignPlan } from '@/api/manage-hooks';
import { networkOnlyRead } from '@/api/client';
import { ApiError } from '@/api/errors';
import { operatorInputError, reportActionError } from '@/lib/report-action-error';
import { uploadCreativePlacement } from '@/api/creative-upload';
import { deliveryTargetFor } from '@/lib/campaign-publish-sequence';
import { patchCreatedCreative } from '@/lib/creative-patch';
import { audienceRepinStrategy, resolveCampaignAudienceDefinition } from '@/lib/campaign-audience-revisions';
import { audienceIdForCreate, audienceTypeFromRules, resolveVisualAudienceRules } from '@/lib/audience-visual-rules';
import { slotMatchesSavedAudience, slotRules, rekeyBySlotId } from '@/lib/campaign-audience-slots';
import { flightValidationIssues, flightsValid } from '@/lib/campaign-flights';
import { campaignIdForCreate, planChangedElsewhereError, creativeIdForCreate, localAssetFingerprint, ownWriteOrChangedElsewhere, planMatchesSavedFields } from '@/lib/campaign-plan';
import { equalAudienceAllocations } from './create-audience-slot';
import { holdRelease } from '@/lib/release-hold';
import type { Audience, CampaignPlan, Creative } from '@/api/types';
import type { CampaignAudienceSegmentInput, CampaignPlanResponse } from '@/api/manage-types';
import type { LocalCreativeAsset } from '@/api/creative-upload';
import type { RequiredIssue } from '@/components/required-field';
import type { CampaignPlanFields, CreativeDraftAsset } from '@/lib/campaign-plan';
import type { CreateFlowSection, CreateFlowState } from './create-flow-state';

/** A creative this wizard created, and what it has done with it since. Kept
 *  per mount so a retried save reuses it instead of creating another. */
export interface MaterializedCreative {
  id: string;
  /** Placement tag -> fingerprint of the local file uploaded there. */
  uploaded: Map<string, string | null>;
  submitted: boolean;
  approved: boolean;
  name: string;
  format: string;
  spec: string;
  destinationUrl: string;
  revision: number;
  /** The creative as the server last returned it; a PATCH is guarded by it. */
  snapshot: Creative;
}

/** A save that must write even when nothing changed, or must fork its audiences. */
interface SaveOptions {
  force?: boolean;
  forceAudienceFork?: boolean;
}

/** A required field and the section that holds it, which validation opens. */
type PlanIssue = RequiredIssue & { section: CreateFlowSection };

/** A picked file is measured before it reaches a slot, so a missing size is a
 *  bug to surface, not a value to invent. */
function uploadableAsset(asset: CreativeDraftAsset): LocalCreativeAsset {
  const width = asset.width ?? asset.w;
  const height = asset.height ?? asset.h;
  if (width == null || height == null) {
    throw new Error(`${asset.name} has no measured size. Pick the file again.`);
  }
  return { ...asset, width, height, durationMs: asset.durationMs ?? undefined };
}

export function campaignPlanSave(flow: CreateFlowState) {
  const { campaign, navigate, queryClient, productionRows,
    availableAudiences, availableCreatives, setStatus, todayStr, production, channel,
    budget, maxEndISO, startDate, endDate, name, audienceSlots, setAudienceSlots,
    unresolvedSegmentPositions, setSlotPreviews, setSlotConfirmed, setCreConfirmed,
    setPreviewCreativeIds, markup, flights, savedPlan, setSavedPlan,
    savedFingerprint, setSavedFingerprint, preflightResult, setPreflightResult, setActionError,
    setActionNotice, required, materializedAudiences, audienceIdByVisualGroup,
    materializedCreatives, saveOperation, plannedCampaignId, attemptedRevision, setSaveInFlight,
    createAudience, createCreative, patchCreative, submitCreativeForReview, approveCreative,
    createCampaign, updateCampaignPlan, setOpen, preflightQuery, 
    setAudienceSyncGate, creativeSelections, unresolvedCreativePositions, previewReviewCurrent,
    formFingerprint, 
  } = flow;

  const materializeAudienceSegments = async ({ forceFork = false } = {}) => {
    const context = { productions: productionRows, audiences: availableAudiences };
    const allocations = equalAudienceAllocations(audienceSlots.length);
    const pinnedAudienceIds = new Set(
      savedPlan?.audience_segments?.map((segment) => segment.audience_id) ?? [],
    );

    const resolved: Audience[] = [];
    for (const [index, slot] of audienceSlots.entries()) {
      // One audience per slot, carrying the rules of every branch inside it.
      // A single-branch slot emits the bare `all` shape the wizard has always
      // posted, so an existing campaign audience still compares equal below
      // instead of forking a duplicate on its first save after this ships.
      const rules = slotRules(slot.groups, context);
      // Derived from this slot's own rules, not declared once for the campaign.
      // The server derives the same answer and refuses a mismatch, so posting a
      // type the tree does not imply is a 422 the operator cannot act on.
      const audienceTypeValue = audienceTypeFromRules(rules);
      const mappedId = audienceIdByVisualGroup.current.get(slot.id);
      const preferredId = mappedId
        ?? (pinnedAudienceIds.has(slot.id) ? slot.id : null);
      const existing = preferredId
        ? materializedAudiences.current.get(preferredId)
          ?? availableAudiences.find((entry) => entry.id === preferredId)
        : null;
      const definitionMatches = !!existing
        && slotMatchesSavedAudience(slot.groups, rules, audienceTypeValue, existing);

      // Never mutate a reusable saved audience from inside a campaign edit.
      // If its definition changed, fork a campaign-owned audience and pin that
      // new revision into the next campaign plan instead.
      const reusable = !forceFork && definitionMatches ? existing : null;
      // Position-keyed, matching `provider_group_name`'s `__AUD{position}`
      // suffix — that correspondence is what an operator joins on when they
      // open Ads Manager beside a report, so the TYPE goes in the description
      // rather than the name, where retyping a slot cannot break it.
      const segmentName = `${name.trim()} · Audience ${index + 1}`;
      const audience = reusable ?? await createAudience.mutateAsync({
        id: audienceIdForCreate(segmentName),
        name: segmentName,
        description: `Campaign audience ${index + 1} of ${audienceSlots.length} (${slot.type}); planned as its own reportable delivery group.`,
        audience_type: audienceTypeValue,
        rules,
      });

      materializedAudiences.current.set(audience.id, audience);
      audienceIdByVisualGroup.current.set(slot.id, audience.id);
      audienceIdByVisualGroup.current.set(audience.id, audience.id);
      resolved.push(audience);

      // Commit each successful mapping immediately. If a later audience
      // request fails, retry reuses these D1 rows instead of orphaning and
      // duplicating them.
      setAudienceSlots((current) => current.map((entry, entryIndex) =>
        (entryIndex === index && entry.id !== audience.id
          ? { ...entry, id: audience.id }
          : entry)));
      // The slot id is the key for its confirmation AND its preview, and both
      // live outside the slot — previews deliberately, so an arriving estimate
      // cannot dirty the save fingerprint. Renaming without moving them orphans
      // both: the section un-ticks itself on first save, and a lone lookalike
      // loses the size it just computed (`audSize` reads slotPreviews[only.id]).
      setSlotConfirmed((current) => rekeyBySlotId(current, slot.id, audience.id));
      setSlotPreviews((current) => rekeyBySlotId(current, slot.id, audience.id));
    }

    // Two slots resolving to one audience would be refused by the server as a
    // repeated audience; catch it here where the message can name the cause.
    const distinct = new Set(resolved.map((audience) => audience.id));
    if (distinct.size !== resolved.length) {
      throw operatorInputError(
        'Two audiences in this campaign resolved to the same definition. Make them different, or remove one.',
      );
    }

    return resolved.map((audience, index): CampaignAudienceSegmentInput => ({
      audience_id: audience.id,
      // One allocation per slot, so every index has one.
      allocation_bps: allocations[index] ?? 0,
    }));
  };

  const materializeCreativeSelections = async ({
    submitForReview = previewReviewCurrent,
    approve = previewReviewCurrent,
  } = {}) => {
    // The creative step renders only after both are picked.
    if (!production || !channel) throw operatorInputError('Select a production and a channel before saving creatives.');
    const resolvedIds = [...creativeSelections.libraryIds];

    for (const [index, draft] of creativeSelections.drafts.entries()) {
      let record = materializedCreatives.current.get(draft.key);
      const fingerprints = new Map(
        draft.placements.map((placement) => [
          placement.placementTag,
          localAssetFingerprint(placement.asset),
        ]),
      );
      const approvedAssetChanged = record?.approved && draft.placements.some(
        (placement) =>
          record?.uploaded.get(placement.placementTag)
          !== fingerprints.get(placement.placementTag),
      );
      const metadataChanged = record && (
        record.name !== draft.name
        || record.format !== draft.format
        || record.spec !== draft.spec
        || record.destinationUrl !== draft.destinationUrl
      );
      if (approvedAssetChanged || (record?.approved && metadataChanged)) record = undefined;

      if (!record) {
        const id = creativeIdForCreate();
        setActionNotice(`Creating local creative ${index + 1} of ${creativeSelections.drafts.length}…`);
        const created = await createCreative.mutateAsync({
          id,
          name: draft.name,
          production_id: production.id,
          channel,
          media_type: draft.mediaType,
          format: draft.format || 'Campaign placement set',
          spec: draft.spec || 'Validated in browser',
          destination_url: draft.destinationUrl,
          agency: null,
        });
        record = {
          id,
          uploaded: new Map(),
          submitted: false,
          approved: false,
          name: draft.name,
          format: draft.format,
          spec: draft.spec,
          destinationUrl: draft.destinationUrl,
          // Carried so the PATCH below can be refused rather than clobber a
          // concurrent edit. `created` is the creative the server stored.
          revision: created?.revision ?? 1,
          snapshot: created,
        };
        materializedCreatives.current.set(draft.key, record);
      } else if (metadataChanged) {
        setActionNotice(`Updating local creative ${index + 1} configuration…`);
        // This is the only creative this wizard may write. `materializedCreatives`
        // is a per-mount map written solely after a successful create above, so
        // `record.id` is always an id minted here; a library creative reaches
        // `libraryIds` instead (see `campaignCreativeSelections`) and is never
        // touched. `creative_patch_never_addresses_a_library_creative` pins that.
        //
        // Two things this send does NOT do, both matching the library drawer
        // (`patchBodyFor` in library.tsx, whose comment carries the history):
        // `spec` goes only when the copy actually moved, because a spec edit is
        // what `edit_requires_republish` keys off and resending it on a rename
        // knocks an approved creative back to review; and `expected_revision`
        // makes a stale draft a 409 rather than a silent overwrite, because
        // `record` is a snapshot from an arbitrarily earlier point in this
        // session and the drawer can have edited the same creative meanwhile.
        await patchCreatedCreative(record, {
          name: draft.name,
          format: draft.format || 'Campaign placement set',
          destination_url: draft.destinationUrl,
          spec: draft.spec,
        }, patchCreative.mutateAsync);
        record.name = draft.name;
        record.format = draft.format;
        record.spec = draft.spec;
        record.destinationUrl = draft.destinationUrl;
        record.revision = record.snapshot.revision;
      }

      for (const [placementIndex, placement] of draft.placements.entries()) {
        const fingerprint = fingerprints.get(placement.placementTag);
        if (record.uploaded.get(placement.placementTag) === fingerprint) continue;
        setActionNotice(
          `Uploading creative ${index + 1}, asset ${placementIndex + 1} of ${draft.placements.length} to file storage…`,
        );
        record.snapshot = await uploadCreativePlacement({
          creativeId: record.id,
          placementTag: placement.placementTag,
          asset: uploadableAsset(placement.asset),
        });
        record.uploaded.set(placement.placementTag, fingerprint ?? null);
      }

      if (submitForReview && !record.submitted) {
        setActionNotice(`Submitting local creative ${index + 1} for review…`);
        await submitCreativeForReview.mutateAsync(record.id);
        record.submitted = true;
      }
      if (approve && !record.approved) {
        setActionNotice(`Recording local approval for creative ${index + 1}…`);
        await approveCreative.mutateAsync(record.id);
        record.approved = true;
      }
      resolvedIds.push(record.id);
    }

    const unique = [...new Set(resolvedIds)];
    return unique;
  };

  const handleCreativeConfirm = async () => {
    setActionError(null);
    // Uploads, creates and review submissions: a new release waits for all of them.
    const releaseHold = holdRelease('campaign_save');
    try {
      setActionNotice('Saving creative drafts and preparing official Meta previews…');
      const ids = channel === 'meta'
        ? await materializeCreativeSelections({ submitForReview: true, approve: false })
        : [];
      if (channel === 'meta') setPreviewCreativeIds(ids);
      setCreConfirmed(true);
      setActionNotice(channel === 'meta' ? 'Meta is generating official placement previews.' : null);
      setOpen((current) => ({ ...current, cr: false, prev: true }));
    } catch (error) {
      setActionError(reportActionError(error, { operation: 'campaign.creative_confirm' }));
    } finally {
      releaseHold();
    }
  };

  const performSaveCampaignPlan = async ({
    force = false,
    forceAudienceFork = false,
  }: SaveOptions = {}): Promise<CampaignPlan> => {
    setActionError(null);
    setActionNotice(null);

    if (savedPlan?.campaign?.status && savedPlan.campaign.status !== 'draft') {
      throw new Error('Ready-to-publish campaigns are immutable. Duplicate this campaign to make another plan.');
    }
    // `operatorInputError`, not `Error`: these are what the operator has yet to
    // fill in, and the reporter skips them by construction rather than by
    // matching message text. The guard above is deliberately a plain Error —
    // reaching it means the UI offered a save it should have disabled.
    if (!production) throw operatorInputError('Select a production before saving.');
    if (!channel) throw operatorInputError('Select a channel before saving.');
    if (!name.trim()) throw operatorInputError('Enter a campaign name before saving.');
    if (!audienceSlots.length) {
      throw operatorInputError('Add at least one audience before saving.');
    }
    // Writing a plan with fewer segments than the one on record would orphan a
    // live provider ad set and truncate its reporting. Refuse, and name the
    // positions that would be lost.
    if (unresolvedSegmentPositions.length) {
      throw operatorInputError(
        `This campaign pins ${unresolvedSegmentPositions.length} audience definition`
        + `${unresolvedSegmentPositions.length === 1 ? '' : 's'} that can no longer be read `
        + `(position ${unresolvedSegmentPositions.join(', ')}). Saving now would drop them. `
        + 'Duplicate the campaign instead.',
      );
    }
    // Same refusal for creatives. A pin we cannot read leaves the copy form
    // showing seeded values where that creative's own copy belongs, so saving
    // would write whatever the form happened to hold.
    if (unresolvedCreativePositions.length) {
      throw operatorInputError(
        `This campaign pins ${unresolvedCreativePositions.length} creative`
        + `${unresolvedCreativePositions.length === 1 ? '' : 's'} that could not be read `
        + `(position ${unresolvedCreativePositions.join(', ')}). The copy shown for `
        + `${unresolvedCreativePositions.length === 1 ? 'it' : 'them'} is not `
        + `${unresolvedCreativePositions.length === 1 ? 'its' : 'their'} own. `
        + 'Reload the campaign before saving.',
      );
    }
    if (!flightsValid(flights, startDate, endDate, budget)) {
      throw operatorInputError('Fix the flight dates and budgets before saving.');
    }

    if (savedPlan && !force && savedFingerprint === formFingerprint) {
      return savedPlan;
    }

    const audienceSegments = await materializeAudienceSegments({
      forceFork: forceAudienceFork,
    });
    const persistedCreativeIds = await materializeCreativeSelections();
    const campaignFields: CampaignPlanFields = {
      name: name.trim(),
      production_id: production.id,
      channel,
      audience_segments: audienceSegments,
      creative_ids: persistedCreativeIds,
      budget_cents: Math.round(budget * 100),
      start_date: startDate,
      end_date: endDate,
      flights: flights.map((flight) => ({
        id: flight.id,
        name: flight.name.trim(),
        start_date: flight.start,
        end_date: flight.end,
        budget_cents: Math.round((+flight.budget || 0) * 100),
      })),
      markup_on: markup,
      starred: campaign?.starred ?? false,
    };

    // A PUT can commit and still fail on the wire (the plan batch lands in D1,
    // then the response is lost). The wizard would otherwise keep the stale
    // expected_revision and every later save would 409 until a hard reload.
    // On a conflict, read the server's current plan: if it is exactly what we
    // just sent, the earlier write was ours — adopt it and keep going.
    //
    // `savedPlan` is the caller's, already known to exist.
    const adoptPlanCommittedWithoutResponse = async (error: unknown, savedPlan: CampaignPlan) => {
      if (!(error instanceof ApiError) || error.status !== 409) throw error;
      let current: CampaignPlan | undefined;
      try {
        const response = await networkOnlyRead<CampaignPlanResponse>(
          `/campaigns/${encodeURIComponent(savedPlan.campaign.id)}/plan`,
        );
        current = response.plan;
      } catch {
        throw error;
      }
      if (!current?.campaign) throw error;
      // This route answers 409 for more than a revision conflict — a campaign
      // that left draft, a creative from another production or channel. Only
      // a revision that moved past ours says a write landed; every other
      // conflict keeps the server's message, which names the real problem.
      if (current.revision <= savedPlan.revision) throw error;
      if (
        current.revision === savedPlan.revision + 1
        && current.campaign.status === 'draft'
        && planMatchesSavedFields(current, campaignFields)
      ) {
        cacheCampaignPlan(queryClient, current);
        return current;
      }
      // Someone else's plan: leave the query cache alone rather than
      // re-baselining a still-mounted wizard onto their revision.
      throw planChangedElsewhereError(current.revision);
    };

    // The create half of the same lost-response problem. The retry reuses this
    // wizard's id, so a POST that committed answers "campaign … exists": read
    // that campaign back and carry on from it instead of stranding a draft
    // nobody is editing. A 409 raised before anything was written (a creative
    // from another production or channel) has no such campaign to read, so the
    // server's own message survives.
    const adoptCampaignCommittedWithoutResponse = async (error: unknown, campaignId: string) => {
      if (!(error instanceof ApiError) || error.status !== 409) throw error;
      let current: CampaignPlan | undefined;
      try {
        const response = await networkOnlyRead<CampaignPlanResponse>(`/campaigns/${encodeURIComponent(campaignId)}/plan`);
        current = response.plan;
      } catch {
        throw error;
      }
      if (!current?.campaign || current.campaign.status !== 'draft') throw error;
      // Recovering means writing over what is there, so it has to be a
      // revision this wizard itself produced. Anything further along was
      // written by someone else who found the draft — and the expected
      // revision below is read from that same plan, so the compare-and-swap
      // cannot catch it.
      if (current.revision > attemptedRevision.current) {
        throw planChangedElsewhereError(current.revision);
      }
      if (planMatchesSavedFields(current, campaignFields)) {
        cacheCampaignPlan(queryClient, current);
        return current;
      }
      // Ours, but the operator changed something between attempts. Continue as
      // an edit of the revision that actually landed.
      attemptedRevision.current = current.revision + 1;
      return ownWriteOrChangedElsewhere(await updateCampaignPlan.mutateAsync({
        id: campaignId,
        plan: { ...campaignFields, expected_revision: current.revision },
      }), current.revision + 1);
    };

    // Every plan this save adopts is the revision its own write produced (or, after a lost
    // response, the recovery's verified equivalent): `savedPlan.revision` is then the one
    // revision the form shows, which a launch and an arm name as reviewed.
    // The write's own answer is checked outside its `try`, so the refusal is never mistaken
    // for a lost response and fed to a recovery.
    type Written = { answered: CampaignPlan } | { recovered: CampaignPlan };
    let written: Written;
    let nextPlan: CampaignPlan;
    if (savedPlan) {
      try {
        written = { answered: await updateCampaignPlan.mutateAsync({
          id: savedPlan.campaign.id,
          plan: {
            ...campaignFields,
            expected_revision: savedPlan.revision,
          },
        }) };
      } catch (error) {
        written = { recovered: await adoptPlanCommittedWithoutResponse(error, savedPlan) };
      }
      nextPlan = 'answered' in written
        ? ownWriteOrChangedElsewhere(written.answered, savedPlan.revision + 1)
        : written.recovered;
    } else {
      if (!plannedCampaignId.current) plannedCampaignId.current = campaignIdForCreate();
      const campaignId = plannedCampaignId.current;
      attemptedRevision.current = Math.max(attemptedRevision.current, 1);
      try {
        written = { answered: await createCampaign.mutateAsync({
          id: campaignId,
          status: 'draft',
          ...campaignFields,
        }) };
      } catch (error) {
        written = { recovered: await adoptCampaignCommittedWithoutResponse(error, campaignId) };
      }
      nextPlan = 'answered' in written ? ownWriteOrChangedElsewhere(written.answered, 1) : written.recovered;
    }

    // From here every save is an edit of `nextPlan`. Dropping the create
    // bookkeeping keeps the reuse above scoped to the campaign it minted, so a
    // future in-place reset of `savedPlan` cannot POST a second campaign's
    // contents under this one's id.
    plannedCampaignId.current = null;
    attemptedRevision.current = 0;
    setSavedPlan(nextPlan);
    setSavedFingerprint(formFingerprint);
    setPreflightResult(null);
    setStatus(nextPlan.campaign.status);
    return nextPlan;
  };

  const saveCampaignPlan = (options: SaveOptions = {}) => {
    if (saveOperation.current) return saveOperation.current;
    setSaveInFlight(true);
    // The whole operation holds a release swap: the uploads, the plan write, and the recovery
    // reads and retry write after a lost response, none of which is a mutation the query
    // client sees between steps. The hold lives in its module, so it outlasts this builder when
    // the operator leaves mid-save (plan frontend-release-update §4.1, 2026-10-21).
    const releaseHold = holdRelease('campaign_save');
    const operation = performSaveCampaignPlan(options);
    saveOperation.current = operation;
    const finish = () => {
      releaseHold();
      if (saveOperation.current === operation) {
        saveOperation.current = null;
        setSaveInFlight(false);
      }
    };
    void operation.then(finish, finish);
    return operation;
  };

  const validateCampaignPlan = () => {
    // No channel/type pairing blocks a save any more: a lookalike on CTV or Display is
    // buildable and reviewable, and only launch is refused. Rule resolution still can fail.
    let audienceMessage: string | null = null;
    // Per slot, so the message names the audience that is broken rather than
    // making the operator find it among several.
    for (const [index, slot] of audienceSlots.entries()) {
      if (audienceMessage) break;
      try {
        resolveVisualAudienceRules(slot.groups, {
          audiences: availableAudiences,
          productions: productionRows,
        });
      } catch (error) {
        const detail = error instanceof Error
          ? error.message
          : 'Add at least one complete audience rule.';
        audienceMessage = audienceSlots.length === 1
          ? detail
          : `Audience ${index + 1}: ${detail}`;
      }
    }

    const issues: Array<PlanIssue | null> = [
      production ? null : {
        key: 'campaign-production',
        message: 'Select the production this campaign should drive.',
        section: 'production',
      },
      channel ? null : {
        key: 'campaign-channel',
        message: 'Select a media channel.',
        section: 'ch',
      },
      name.trim() ? null : {
        key: 'campaign-name',
        message: 'Enter a campaign name.',
        section: 'rev',
      },
      audienceMessage ? {
        key: 'campaign-audience',
        message: audienceMessage,
        section: 'aud',
      } : null,
      ...flightValidationIssues(flights, startDate, endDate, budget, { storedStartDate: campaign?.startDate ?? null, todayStr, maxEndISO }).map((issue): PlanIssue => ({
        ...issue,
        section: 'sched',
      })),
    ];
    const first = issues.find((issue) => issue !== null);
    if (first) {
      setOpen((current) => ({ ...current, [first.section]: true }));
    }
    const valid = required.report(issues);
    if (!valid) {
      const count = new Set(issues.flatMap((issue) => (issue ? [issue.key] : []))).size;
      setActionError(`Fix ${count} required ${count === 1 ? 'item' : 'items'} highlighted below before saving.`);
    }
    return valid;
  };

  const handleSaveDraft = async () => {
    if (!validateCampaignPlan()) return;
    try {
      const nextPlan = await saveCampaignPlan({ force: true });
      setActionNotice('Draft saved.');
      if (!campaign?.id) {
        await navigate({
          to: '/campaigns/$campaignId',
          params: { campaignId: nextPlan.campaign.id },
        });
      }
    } catch (error) {
      setActionError(reportActionError(error, { operation: 'campaign.save_draft' }));
    }
  };

  const handlePrepareReady = async () => {
    if (!validateCampaignPlan()) return;
    try {
      const currentPreflight = preflightResult ?? preflightQuery.data;
      const repin = audienceRepinStrategy(currentPreflight?.checks);
      const nextPlan = await saveCampaignPlan({
        force: repin.forceSave,
        // Seed pins belong to the immutable audience revision, not merely the
        // campaign revision. Forking preserves the reviewed rule tree while
        // capturing the seed's new revision without mutating shared audiences.
        forceAudienceFork: repin.forceAudienceFork,
      });
      setPreflightResult(null);
      setActionNotice(
        'Draft saved. Waiting for each audience revision to finish materializing.',
      );
      setAudienceSyncGate({
        campaignId: nextPlan.campaign.id,
        items: nextPlan.audience_segments.map((segment, index) => {
          const definition = resolveCampaignAudienceDefinition(
            segment,
            nextPlan.audience_revisions,
            availableAudiences,
          );
          return {
            id: segment.audience_id,
            label: definition?.name || `Campaign audience ${index + 1}`,
            revision: segment.audience_revision,
            target: deliveryTargetFor(channel),
          };
        }),
        creativeItems: [...new Set(
          nextPlan.creatives.map((creative) => creative.creative_id),
        )].map((creativeId, index) => {
          const libraryCreative = availableCreatives.find(
            (creative) => creative.id === creativeId,
          );
          const materializedCreative = [...materializedCreatives.current.values()]
            .find((creative) => creative.id === creativeId);
          return {
            id: creativeId,
            label: libraryCreative?.name
              || materializedCreative?.name
              || `Campaign creative ${index + 1}`,
            target: deliveryTargetFor(channel),
          };
        }),
      });
    } catch (error) {
      setActionError(reportActionError(error, { operation: 'campaign.prepare_ready' }));
    }
  };

  return {
    materializeAudienceSegments,
    materializeCreativeSelections,
    handleCreativeConfirm,
    performSaveCampaignPlan,
    saveCampaignPlan,
    validateCampaignPlan,
    handleSaveDraft,
    handlePrepareReady,
  };
}
export type CampaignPlanSave = ReturnType<typeof campaignPlanSave>;
